// Tiny, dependency-free flow data + sender validation.
// Split out of flow_copy.jsx / screens.jsx so the router (site.jsx) can know
// the step paths and validate the sender shape WITHOUT pulling the whole
// stage UI, the seven-screen workbench, or the EN/ES/HT copy into the initial
// download. See scripts/check_bundle.js — every kilobyte here ships to every
// visitor on first paint, frequently on metered mobile data.

// ── The governed send flow (WEB-02 §15.2, TARGET production order) ─────────
// recipient/facility → message/photo → rules → review → checkout →
// confirmation. Every step owns its own route; /send/message and /send/photo
// are direct views within the same message/photo business step — navigating
// between them preserves the in-progress draft and never creates an order or
// uploads twice.
export const FLOW_STEPS = [
  { k: "facility", path: "/send/facility" },
  { k: "recipient", path: "/send/recipient" },
  { k: "photo", path: "/send/photo" },
  { k: "message", path: "/send/message" },
  { k: "rules", path: "/send/rules" },
  { k: "review", path: "/send/review" },
  { k: "checkout", path: "/checkout" },
];

// Deep links and bookmarks may still point at older routes; each resolves
// into the step that now owns it. The old canonical stage paths resolve too:
// /send/create held photo+message (lands on photo), /send/review was the
// combined rules+preview stage (lands on the dedicated rules step, whose
// acknowledgment gates the review).
export const LEGACY_STEP_TO_STAGE = {
  photo: 2,
  message: 3,
  facility: 0,
  recipient: 1,
  rules: 4,
  review: 5,
  checkout: 6,
  create: 2,
};

// Path → step index for every route the flow answers, canonical and legacy.
export const PATH_TO_STEP = (() => {
  const map = {};
  FLOW_STEPS.forEach((s, i) => { map[s.path] = i; });
  map["/send"] = 0;
  map["/send/"] = 0;
  map["/send/create"] = 2; // old canonical stage: photo+message
  return map;
})();

// The empty return address. Kept here so the wizard, the workbench surface and
// the preview all start from the same shape.
export const EMPTY_SENDER = { name: "", line1: "", line2: "", city: "", state: "", zip: "" };

// Mirrors the server's checks in create_guest_order, field for field. Kept in
// step so Continue is never enabled for something the API answers 400 to — the
// customer would otherwise reach Pay, press it, and be told their own address
// is wrong on the screen furthest from where they typed it.
// The return address is REQUIRED on every order (owner decision
// 2026-10-31, §20.27 BUILD-27). It was required once, then made optional
// when the current-source review withdrew the claim that BSO publishes a
// rule requiring one — the handbook wording is addressed to people in
// custody, and no BSO source says what an outside service must print. The
// owner has since directed that a return address is required for the
// postcard; it is not an option.
//
// Since 2026-10-29 (owner decision, §20.24 BUILD-24) the address prints
// on the card as its return address, and CareCard does not keep it after
// mailing. A half-typed address is printed nowhere useful and returns to
// nobody, so every field is validated the same way the server validates.
const SENDER_RULES = {
  name:  (v) => v.trim().length >= 2  || "Enter the name the card should come back to.",
  line1: (v) => v.trim().length >= 3  || "Enter your street address.",
  city:  (v) => v.trim().length >= 2  || "Enter your city.",
  state: (v) => /^[A-Za-z]{2}$/.test(v.trim()) || "Two-letter state, e.g. FL.",
  zip:   (v) => /^\d{5}(-\d{4})?$/.test(v.trim()) || "Five digits, or ZIP+4.",
};

/** True when the customer has started filling in a return address at all. */
export function senderStarted(sender) {
  return ["name", "line1", "city", "state", "zip"].some((k) => (sender?.[k] || "").trim());
}

/** Field errors for the return address. The address is required, so an
 *  untouched, entirely empty one is five missing fields, not a valid
 *  order. Callers render each error only after its field has been
 *  touched, so a fresh form does not open with five alerts. */
export function senderErrors(sender) {
  const out = {};
  for (const [k, rule] of Object.entries(SENDER_RULES)) {
    const r = rule(sender?.[k] || "");
    if (r !== true) out[k] = r;
  }
  return out;
}

export const senderComplete = (sender, facility) =>
  Object.keys(senderErrors(sender)).length === 0 && !senderPoBoxProblem(sender, facility);

// ── V50 (2026-11-04): per-facility recipient rules ──────────────────────
// St. Lucie County Jail (SLCSO) mail goes to Smart Communications' scanning
// center: no housing line, a plain numeric jail ID (4–6 digits), the sender's
// physical street address (no P.O. box), and the person sees a scan on a
// tablet. BSO facilities are unchanged. The live facility row from
// /api/guest/facilities carries these as data; the slug check is the
// offline fallback.
export const ST_LUCIE_SLUG = "st-lucie-county-jail";

export function isScanFacility(f) {
  return Boolean(f) && (f.delivery_mode === "scanned_to_tablet" || f.slug === ST_LUCIE_SLUG);
}

export function facilityNeedsHousing(f) {
  if (!f) return true;
  if (typeof f.housing_required === "boolean") return f.housing_required;
  return f.slug !== ST_LUCIE_SLUG;
}

export function facilityIdRule(f) {
  return f && (f.id_rule === "st_lucie" || f.slug === ST_LUCIE_SLUG) ? "st_lucie" : "bso";
}

/** People paste IDs with spaces, dashes or a leading "#": keep the digits. */
export function normalizeRecipientId(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/[\s#-]/g, "");
  return /^\d+$/.test(digits) ? digits : raw;
}

export function recipientIdOk(f, value) {
  if (facilityIdRule(f) === "st_lucie") return /^\d{4,6}$/.test(normalizeRecipientId(value));
  return ARREST_RE.test(String(value || "").trim());
}

const PO_BOX_RE = /\b(?:p\.?\s*o\.?\s*box|post\s*office\s*box|p\.?\s*o\.?\s*b\.?(?=\s*\d)|pmb|private\s*mail\s*box|box\s*#?\s*\d+|apdo|caixa\s*postal)\b/i;

export function looksLikePoBox(line) {
  return PO_BOX_RE.test(String(line || ""));
}

/** True when this facility needs a physical street address and the sender
 *  typed a P.O. box / mailbox service instead. */
export function senderPoBoxProblem(sender, facility) {
  if (!isScanFacility(facility)) return false;
  return looksLikePoBox(sender?.line1) || looksLikePoBox(sender?.line2);
}

// ── Draft validation (WEB-02 §15.2 + §5.1–§5.6) ────────────────────────────
// Pure functions over the draft shape so the router can enforce prerequisite
// state on direct route entry ("redirect/recover safely rather than skipping
// required validation") and so the rules are unit-testable without the UI.

const ARREST_RE = /^[\w\-]{4,20}$/;

/** Unicode grapheme-cluster count — §5.3 validates 1–500 graphemes, not code
 *  units, so emoji and combining marks count the way a person counts them. */
export function graphemeLength(value) {
  const s = String(value || "");
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    return [...new Intl.Segmenter().segment(s)].length;
  }
  return [...s].length;
}

/** Per-step completion for the draft. Step indices follow FLOW_STEPS. */
export function stepComplete(draft, step) {
  switch (step) {
    case 0: // facility — §5.1: a selected, orderable facility (policy gate)
      return Boolean(draft?.facility) && draft.facility.unavailable !== true;
    case 1: // recipient — §5.2: name, arrest/CIS number, housing; return address required
      return Boolean(draft?.facility)
        && (draft?.name || "").trim().length >= 2
        && (draft?.name || "").trim().length <= 50
        && recipientIdOk(draft?.facility, draft?.arrest)
        && (!facilityNeedsHousing(draft?.facility) || (draft?.housing || "").trim().length >= 1)
        && senderComplete(draft?.sender, draft?.facility);
    case 2: // photo — §5.4: a server-claimed upload, not a local-only preview
      return Boolean(draft?.image && draft.image.id);
    case 3: // message — §5.3: required, trimmed, 1–500 grapheme clusters
      return graphemeLength((draft?.message || "").trim()) >= 1
        && graphemeLength((draft?.message || "").trim()) <= 500;
    case 4: // rules — §5.6: policy acknowledgment AND likeness attestation,
            // recorded as separate statements
      return draft?.ack === true && draft?.likenessAttested === true;
    case 5: // review — no input of its own; reachable once rules are done
      return stepComplete(draft, 4);
    default:
      return false;
  }
}

/** Index of the first incomplete step — the furthest a direct route entry may
 *  land. Anything past it redirects back here instead of skipping required
 *  validation. */
export function firstIncompleteStep(draft) {
  for (let i = 0; i <= 5; i++) {
    if (!stepComplete(draft, i)) return i;
  }
  return FLOW_STEPS.length - 1; // checkout
}
